/**
 * Turning "Allow multiple" on or off — the owner's ruling B16 (keepr-api
 * utils/shapeChange.js, docs/SCHEMA.md "Turning Allow multiple on or off"), on
 * every type whose switch makes the stored value a LIST: a Choice, a date, a
 * date and time, an item lookup, a user. (This was utils/choiceShape.js while
 * B16 reached only the Choice.) The switch changes the SHAPE of every stored
 * value (one value, or a list), and stored data is never out of sync with its
 * definition, so the API refuses the flip (409 `values_need_conversion`)
 * while items hold values in the old shape, until the element carries the
 * author's answer:
 *
 *   on   options.convertValues: 'wrap'  — each single value becomes a list of one
 *   off  options.convertValues: 'first' — each item keeps ONE entry (a Choice
 *        its first in the choices' order, a date its earliest, an item lookup
 *        or a user its first stored), or the author reviews those items first
 *
 * A measurement that allows several measures (KPR-1085) asks a third
 * question: a measure taken away while items hold values in it waits for
 * `clear` (measuresRemoved, MEASURES_CLEAR). Its own units (KPR-1109) ask a
 * fourth: an own unit taken away while items hold values in it waits for
 * `clear` here (ownUnitsRemoved) -- the API also takes `move` with a unit map,
 * which the card editor offers once the Change card mapping step can draw it.
 *
 * An element MADE AN ITEM LOOKUP (KPR-1136, the owner's ruling of
 * 2026-10-22) asks a fifth: retyped into a card lookup, or a lookup added
 * under a name items still hold values under (a removed element's values stay
 * on the items, hidden), while those values exist — the API's 409
 * `type_change_needs_empty` with `answer: 'clear'` — waits for `clear`, which
 * empties them when the card saves (madeLookup, LOOKUP_CLEAR). A value typed
 * as text cannot be told from a link, so emptying is the only answer.
 *
 * This is the browser's mirror of WHAT counts as a flip (shapeDirection),
 * the words each type is spoken of in (shapeWords), the instruction's name,
 * the door the refusal carries, and the history sentence. The API owns the
 * rule and the counts.
 */

import { allowedMeasures, isOwnUnitId, ownUnitsOf } from './units';

export const SHAPE_INSTRUCTION_KEY = 'convertValues';
export const SHAPE_CONVERSION = { on: 'wrap', off: 'first' };
export const SHAPE_REFUSAL_CODE = 'values_need_conversion';
// A file element's third answer (the 10b contract): "Photos only" turned on
// while bound files are not photos — those files move to each item's Other
// attachments (never deleted) when the card is saved.
export const FILE_DETACH = 'detach';
// A measurement's answer (KPR-1085): a measure taken away from an element that
// allows several, while items hold values in it -- those values are emptied
// when the card is saved; the values in the measures it keeps stay.
export const MEASURES_CLEAR = 'clear';
// An element made an item lookup over held values (KPR-1136): the same word.
export const LOOKUP_CLEAR = 'clear';
const LOOKUP = 'card-lookup';
// The code an element made an item lookup over held values is refused with (G8's).
const LOOKUP_REFUSAL_CODE = 'type_change_needs_empty';
// The types whose own retype the API refuses outright while values exist
// (G8; utils/fileValues.js TYPE_CHANGE_NEEDS_EMPTY_TYPES): out of one of these,
// a lookup is G8's question, with no answer to give.
const NO_ANSWER_FROM = ['file', 'user', 'location', 'confirmations', 'repeating-group'];

/**
 * Is `element` an item lookup it was not, against its saved counterpart — a
 * type changed into a card lookup from one the API answers with `clear`, or a
 * lookup with no saved element of its name (added, or added back)? keepr-api
 * shapeChange madeLookup. Whether items hold values is the API's to say.
 */
export function madeLookup(element, savedElement) {
	if (!element || element.dataType !== LOOKUP) return false;
	if (!savedElement) return true;
	const from = savedElement.dataType || 'text-small';
	return from !== LOOKUP && !NO_ANSWER_FROM.includes(from);
}

/** Does a refusal ask the lookup's `clear` (409 type_change_needs_empty, answer 'clear')? */
export const refusalAsksLookupClear = entry => !!entry && entry.to === LOOKUP
	&& (entry.answer === LOOKUP_CLEAR || (Array.isArray(entry.conversions) && entry.conversions.includes(LOOKUP_CLEAR)));

// The types whose `allowMultiple` stores a list (keepr-api LIST_TYPES). A
// file's list (PR 10b) keeps its first file on `first`; the others move to
// each item's Other attachments rather than being dropped.
export const SHAPE_LIST_TYPES = ['choice', 'date', 'date-time', 'card-lookup', 'user', 'file'];

// What one entry of each list type is called, and which entry `first` keeps
// (keepr-api shapeChange FAMILIES — the same words, so the dialog, the note
// on the element and the history sentence agree with the API's own).
const FAMILIES = {
	choice: { one: 'choice', many: 'choices', keeps: 'first' },
	date: { one: 'date', many: 'dates', keeps: 'earliest' },
	'date-time': { one: 'date and time', many: 'dates and times', keeps: 'earliest' },
	'card-lookup': { one: 'linked item', many: 'linked items', keeps: 'first' },
	user: { one: 'user', many: 'users', keeps: 'first' },
	file: { one: 'file', many: 'files', keeps: 'first' },
};

/**
 * "Photos only" turned on over a saved file element that took any file — the
 * definition change the API refuses (409 values_need_conversion) while bound
 * files are not photos, until the element carries `convertValues: 'detach'`.
 */
export function fileAcceptTightened(element, savedElement) {
	if (!element || !savedElement) return false;
	if (element.dataType !== 'file' || savedElement.dataType !== 'file') return false;
	return element.options?.accept === 'image' && savedElement.options?.accept !== 'image';
}

/**
 * The one answer an element's `convertValues` may carry against its saved
 * counterpart: the shape flip's ('wrap' / 'first') first, else 'detach' for a
 * file element turned "Photos only", else none. An answer that is not this
 * one is stale and never travels. (A save that flips "Allow multiple" AND
 * turns on "Photos only" asks the flip first; the API's next refusal asks
 * the other.)
 */
export function expectedConversion(element, savedElement) {
	// Made an item lookup: `clear` supersedes a list's wrap or first (KPR-1136).
	if (madeLookup(element, savedElement)) return LOOKUP_CLEAR;
	const shape = shapeDirection(element, savedElement);
	if (shape) return SHAPE_CONVERSION[shape];
	if (fileAcceptTightened(element, savedElement)) return FILE_DETACH;
	return measuresRemoved(element, savedElement).length || ownUnitsRemoved(element, savedElement).length ? MEASURES_CLEAR : null;
}

/**
 * The measures a save takes away from a measurement element that allows (or
 * allowed) several, or [] (keepr-api shapeChange removedMeasures): a list made
 * shorter, turned off, or left out. A one-measure element whose measure
 * changes is not this rule's (the API's measure_immutable).
 */
export function measuresRemoved(element, savedElement) {
	if (!element || !savedElement || element.dataType !== 'measurement' || savedElement.dataType !== 'measurement') return [];
	// The registry measures: own units (KPR-1109) are ownUnitsRemoved's.
	const before = allowedMeasures(savedElement.options || {}).filter(id => !isOwnUnitId(id));
	const after = allowedMeasures(element.options || {}).filter(id => !isOwnUnitId(id));
	if (!before.length || !after.length || (before.length < 2 && after.length < 2)) return [];
	return before.filter(id => !after.includes(id));
}

/**
 * The own units (KPR-1109) a save takes away from a measurement element, by
 * id: [{ id, singular, plural }] (keepr-api shapeChange removedOwnUnits). A
 * rename keeps the id, so it is no removal.
 */
export function ownUnitsRemoved(element, savedElement) {
	if (!element || !savedElement || element.dataType !== 'measurement' || savedElement.dataType !== 'measurement') return [];
	const kept = new Set(ownUnitsOf(element.options || {}).map(own => own.id));
	return ownUnitsOf(savedElement.options || {}).filter(own => !kept.has(own.id));
}

/** Does a refusal ask a measurement's `clear` (a measure taken away)? An item lookup's is refusalAsksLookupClear. */
export const refusalAsksClear = entry => !!entry && (entry.direction === 'measures' || (entry.answer === MEASURES_CLEAR && !refusalAsksLookupClear(entry)));

/** Does a refusal ask about an own unit taken away (KPR-1109: move or clear)? */
export const refusalAsksOwnUnits = entry => !!entry && entry.direction === 'ownUnits';

/**
 * The answers a `convertValues` holds. The 10b contract: a string, or an
 * array when one save needs two answers (`['first', 'detach']`: "Allow
 * multiple" turned off AND "Photos only" turned on).
 */
export function conversionAnswers(value) {
	if (Array.isArray(value)) return value.filter(answer => typeof answer === 'string' && answer);
	return typeof value === 'string' && value ? [value] : [];
}

/**
 * Every answer an element's save needs against its saved copy, in the order
 * the API applies them: the multiplicity first (`wrap` / `first`), then
 * "Photos only" (`detach`).
 */
export function expectedConversions(element, savedElement) {
	// Made an item lookup (KPR-1136): `clear` alone — nothing is left to wrap.
	if (madeLookup(element, savedElement)) return [LOOKUP_CLEAR];
	const out = [];
	const shape = shapeDirection(element, savedElement);
	if (shape) out.push(SHAPE_CONVERSION[shape]);
	if (fileAcceptTightened(element, savedElement)) out.push(FILE_DETACH);
	// One `clear` answers a measure and an own unit taken away alike.
	if (measuresRemoved(element, savedElement).length || ownUnitsRemoved(element, savedElement).length) out.push(MEASURES_CLEAR);
	return out;
}

/**
 * What an element's `convertValues` carries once `add` is answered: the
 * answers it already holds and `add`, kept only while the element still
 * needs each (a switch turned back drops its answer), in the fixed order.
 * One answer is a string, two an array, none undefined (the key goes).
 */
export function mergeConversionAnswers(current, add, element, savedElement) {
	const have = new Set([...conversionAnswers(current), ...conversionAnswers(add)]);
	const kept = expectedConversions(element, savedElement).filter(answer => have.has(answer));
	if (!kept.length) return undefined;
	return kept.length === 1 ? kept[0] : kept;
}

const sameAnswers = (a, b) => conversionAnswers(a).join('|') === conversionAnswers(b).join('|')
	&& Array.isArray(a) === Array.isArray(b);

/**
 * Which question a refusal asks: 'detach' (a file element turned "Photos
 * only") or the shape flip's. The API's entry may say so itself (`answer` or
 * `direction` 'detach'); otherwise the element against its saved copy does.
 */
export function refusalAsksDetach(entry, element = null, savedElement = null) {
	if (!entry) return false;
	if (entry.answer === FILE_DETACH || entry.direction === FILE_DETACH || entry.direction === 'accept') return true;
	if (entry.direction === 'on' || entry.direction === 'off') return false;
	return fileAcceptTightened(element, savedElement);
}

/** Does this element store a LIST? */
export const listShaped = element => !!element && SHAPE_LIST_TYPES.includes(element.dataType) && element.options?.allowMultiple === true;

/**
 * 'on' (one value becomes a list), 'off' (a list becomes one value) or null —
 * an element against its saved counterpart: the switch itself, or a type
 * change into or out of a list type that allows multiple. A new element (no
 * saved one) never flips.
 */
export function shapeDirection(element, savedElement) {
	if (!element || !savedElement) return null;
	const was = listShaped(savedElement);
	const now = listShaped(element);
	if (was === now) return null;
	return now ? 'on' : 'off';
}

/**
 * The words a conversion is spoken of in, from the refusal's (or summary's)
 * own types: turning ON, the type the list will hold; turning OFF, the new
 * type while it is a Choice, else the one the lists were written under (a
 * multi date made a short text keeps its earliest DATE) — keepr-api familyOf.
 * -> { type, one, many, keeps }
 */
export function shapeWords({ direction, allowMultiple, from, to } = {}) {
	const on = direction ? direction === 'on' : allowMultiple === true;
	const type = on ? to : (to === 'choice' ? 'choice' : from);
	return { type, ...(FAMILIES[type] || FAMILIES.choice) };
}

/**
 * The refusal entry for one element, from a 409's payload or a preview's
 * `refusal`: B16's `values_need_conversion`, or an element made an item
 * lookup over held values that the author can answer (KPR-1136: 409
 * `type_change_needs_empty` with `answer: 'clear'` — G8's refusal without one
 * is no question to ask, and stays the save's error). A lookup's entry counts
 * in `count`; it is read as `items`, as the other entries count.
 */
export function shapeRefusalFor(refusal, elementName) {
	if (!refusal) return null;
	if (refusal.code === LOOKUP_REFUSAL_CODE) {
		const entries = (Array.isArray(refusal.changes) && refusal.changes.length ? refusal.changes : [refusal]).filter(refusalAsksLookupClear);
		const entry = (elementName ? entries.find(e => e?.element === elementName) : null) || entries[0] || null;
		if (!entry) return null;
		const review = entry.element === refusal.element ? refusal.review : null;
		return { ...entry, items: entry.items ?? entry.count, ...(review ? { review } : {}) };
	}
	if (refusal.code !== SHAPE_REFUSAL_CODE) return null;
	const entries = Array.isArray(refusal.changes) && refusal.changes.length ? refusal.changes : [refusal];
	return (elementName ? entries.find(entry => entry?.element === elementName) : null) || entries[0] || null;
}


/** Is this a refusal the shape dialog answers (B16's, or an item lookup's `clear`)? */
export const isShapeRefusal = refusal => !!shapeRefusalFor(refusal, refusal?.element);

/**
 * The list the "Review first" path opens: the collection's items filtered to
 * exactly the ids the API listed (it builds the whole query — lesson
 * doors-to-a-filtered-list). null when there is no collection to open it in.
 */
export function shapeReviewPath(review, fallbackCollectionId = null) {
	const collectionId = review?.collectionId || fallbackCollectionId;
	if (!review?.query || !collectionId) return null;
	const params = new URLSearchParams();
	params.set('q', review.query);
	return `/collections/${collectionId}/items?${params.toString()}`;
}

/**
 * "skills now holds one choice: 12 items converted (each keeps its first
 * choice; 4 held more than one)" / "visits now holds a list: 3 items
 * converted (each single date made a list of one)" — keepr-api shapeChange
 * describeConversion, verbatim, for the save toast.
 */
export function describeShapeConversion(conversion) {
	const c = conversion || {};
	const n = v => Number(v || 0).toLocaleString('en-US');
	const noun = c.converted === 1 ? 'item' : 'items';
	const words = shapeWords({ allowMultiple: c.choice === 'wrap', from: c.from, to: c.to });
	// A file element (PR 10b): "Photos only" moved the files that are not
	// photos, and "first" moved the others — to Other attachments, never
	// deleted.
	if (c.choice === FILE_DETACH) {
		return `${c.element} now takes photos only: ${n(c.converted)} ${noun} converted (the files that are not photos moved to Other attachments)`;
	}
	// An own unit taken away (KPR-1109): keepr-api describeConversion, verbatim.
	if (Array.isArray(c.ownUnits)) {
		const goes = c.ownUnits.map(own => (own.to ? `${own.singular} to ${own.to.label}` : `${own.singular} emptied`)).join(', ');
		let changed = `${c.element} no longer has ${c.ownUnits.map(own => own.singular).join(' or ')}: ${n(c.converted)} ${noun} changed (${goes}; ${n(c.moved)} moved, keeping their numbers, ${n(c.cleared)} emptied)`;
		if (c.incomplete) changed += ' — stopped part-way (finish with the convertMultiValues maintenance task)';
		return changed;
	}
	// An element made an item lookup (KPR-1136): keepr-api describeConversion, verbatim.
	if (c.lookup && c.unsaved) {
		return `${c.element}: ${n(c.converted)} ${noun} cleared for it to become an item lookup, and the change did not save${c.incomplete ? ' (the clearing stopped part-way)' : ''}`;
	}
	if (c.lookup) {
		let text = `${c.element} is now an item lookup: ${n(c.converted)} ${noun} cleared (${c.converted === 1 ? 'the value it held' : 'the values they held'} emptied)`;
		if (c.skipped) {
			const reasons = Object.entries(c.skippedReasons || {}).map(([k, v]) => `${n(v)} ${String(k).replace(/_/g, ' ')}`);
			text += `, ${n(c.skipped)} skipped${reasons.length ? ` (${reasons.join(', ')})` : ''}`;
		}
		if (c.incomplete) text += ' — stopped part-way';
		return text;
	}
	// A measure taken away (KPR-1085): keepr-api describeConversion, verbatim.
	if (c.choice === MEASURES_CLEAR) {
		const removed = (c.removed || []).join(' or ') || 'a measure';
		let cleared = `${c.element} no longer allows ${removed}: ${n(c.converted)} ${noun} cleared (${c.converted === 1 ? 'its value' : 'their values'} in ${(c.removed || []).join(' or ') || 'that measure'} emptied; ${(c.measures || []).join(', ')} kept)`;
		if (c.incomplete) cleared += ' — stopped part-way (finish with the convertMultiValues maintenance task)';
		return cleared;
	}
	let text = c.choice === 'wrap'
		? `${c.element} now holds a list: ${n(c.converted)} ${noun} converted (each single ${words.one} made a list of one)`
		: `${c.element} now holds one ${words.one}: ${n(c.converted)} ${noun} converted (each keeps its ${words.keeps} ${words.one}${c.multiple ? `; ${n(c.multiple)} held more than one` : ''}${words.type === 'file' && c.multiple ? ', the others moved to Other attachments' : ''})`;
	if (c.skipped) {
		const reasons = Object.entries(c.skippedReasons || {}).map(([k, v]) => `${n(v)} ${String(k).replace(/_/g, ' ')}`);
		text += `, ${n(c.skipped)} skipped${reasons.length ? ` (${reasons.join(', ')})` : ''}`;
	}
	if (c.incomplete) text += ' — stopped part-way (finish with the convertMultiValues maintenance task)';
	return text;
}

/**
 * The elements without a stale answer: `convertValues` travels only while the
 * element still flips the way it was given for (the switch turned back, or
 * the type changed again, drops it). `names[i]` is the name element i saves
 * under — the key of its saved counterpart in `savedByName`.
 */
export function dropStaleShapeInstructions(elements, savedByName, names) {
	return (elements || []).map((element, i) => {
		if (!element?.options || !(SHAPE_INSTRUCTION_KEY in element.options)) return element;
		const kept = keptConversion(element, savedByName?.[names?.[i]]);
		if (sameAnswers(kept, element.options[SHAPE_INSTRUCTION_KEY])) return element;
		const options = { ...element.options };
		if (kept === undefined) delete options[SHAPE_INSTRUCTION_KEY];
		else options[SHAPE_INSTRUCTION_KEY] = kept;
		return { ...element, options };
	});
}

/**
 * The `convertValues` an element may send against its saved copy: only the
 * answers it still needs, in the fixed order (one a string, two an array),
 * or undefined. A stale answer never travels.
 */
export function keptConversion(element, savedElement) {
	return mergeConversionAnswers(element?.options?.[SHAPE_INSTRUCTION_KEY], null, element, savedElement);
}

/** Does `convertValues` already hold exactly what `keptConversion` would send? */
export const conversionIsCurrent = (element, savedElement) => sameAnswers(
	keptConversion(element, savedElement),
	element?.options?.[SHAPE_INSTRUCTION_KEY]
);
